import get from 'lodash/get'

/**
 * All the validators are derived from the OKTA documentation for
 * the Password Complexity Object here:
 *
 *   https://developer.okta.com/docs/api/resources/policy/#complexity-object
 *
 * Note that the requirements for `minSymbol` are vague on what symbols they
 * explicitly allow, so our validator here uses the set from their example
 * as we have nothing else to go on.
 */

const noop = () => undefined
const matcher = (value, expression) => (value.match(expression) || []).length > 0

const COMPLEXITY = {
  minLength: {
    validator: (count, { password }) => password.length >= count,
    message: count => `Be at least ${count} characters in length`,
  },
  minLowerCase: {
    validator: (enabled, { password }) => (!!enabled ? matcher(password, /[a-z]/g) : undefined),
    message: enabled => !!enabled && 'Include at least one lowercase letter' || undefined,
  },
  minUpperCase: {
    validator: (enabled, { password }) => (!!enabled ? matcher(password, /[A-Z]/g) : undefined),
    message: enabled => !!enabled && 'Include at least one uppercase letter' || undefined,
  },
  minNumber: {
    validator: (enabled, { password }) => (!!enabled ? matcher(password, /[0-9]/g) : undefined),
    message: enabled => !!enabled && 'Include at least one number' || undefined,
  },
  minSymbol: {
    validator: (enabled, { password }) => (!!enabled ? matcher(password, /[\!\@#\$%\^&\*]/g) : undefined),
    message: enabled => !!enabled && 'Include at least one symbol the following: "!", "@", "#", "$", "%", "^", "&", "*", "?"' || undefined,
  },
  excludeUsername: {
    validator: (enabled, { password, username }) => (!!enabled ? !password.includes(username) : undefined),
    message: enabled => !!enabled && 'Not include your username' || undefined,
  },
}

/** If you want to explicitly disable any rules for whatever reason, add its key here */
const UNIMPLEMENTED_RULES = [
  // 'excludeUsername', 'minSymbol', 'minUpperCase', 'minLowerCase',
]

const isEnabled = (rules, rule) => Boolean(get(rules, rule, 0))
const isImplemented = rule => !UNIMPLEMENTED_RULES.includes(rule)

const filterDisabledRules = rules => ([rule]) => isEnabled(rules, rule)
const filterImplementedRules = ([rule]) => isImplemented(rule)

const filterEnabled = rules => Object.entries(rules)
  .filter(filterDisabledRules(rules))
  .filter(filterImplementedRules)

const _getComplexityProp = (rule, type) => get(COMPLEXITY, [rule, type], noop)

const getValidator = rule => _getComplexityProp(rule, 'validator')
const getMessage = rule => _getComplexityProp(rule, 'message')

export const createPasswordComplexityList = (rules, password, username) => filterEnabled(rules).map(
  ([rule, ...params]) => ({
    valid: getValidator(rule)(...params, { password, username }),
    message: getMessage(rule)(...params),
  })
)

export const isComplexPassword = (rules, password, username) => !filterEnabled(rules).map(([rule, ...params]) => getValidator(rule)(...params, { password, username })).includes(false)
